import DeleteIcon from '@mui/icons-material/Delete';
import { Button, TextField } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { FaUpload } from 'react-icons/fa';
import { useUser } from '../../../../../hooks/useUser';
import { fetchAndSetUser } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { updateUser } from '../../../../../services/services';
import VyAvatar from '../../../../common/VyAvatar';
import { VyUploadCrop } from '../../../../common/VyUploadCrop';
import { ProfileFileManagement } from './ProfileFileManagement';
import { ProfileRegistrationFields } from './ProfileRegistrationFields';
import { FormState } from 'react-hook-form';
import { FieldValues } from 'react-hook-form';
import { toast } from 'react-hot-toast';

interface ProfileFormModalInterface {
  background?: string;
  opacity?: number;
  textColor?: string;
  hideTitle?: boolean;
  hideAvatar?: boolean;
  onFormStateChange?: (formState: FormState<FieldValues>) => void;
}

const ProfileFormModal = ({
  background = '#FFFFFF',
  opacity = 1,
  textColor = '#000000',
  hideTitle = false,
  hideAvatar = false,
  onFormStateChange,
}: ProfileFormModalInterface) => {
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.common.event);
  const registration = useSelector((state) => state.common.eventRegistration);
  const guestmanagementFields = useSelector((state) => state.common.registrationFields);

  const dispatch = useDispatch();
  const { t } = useTranslation(['event', 'toasts']);
  const { uploadAvatar } = useUser();

  const [userName, setUserName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');

  async function updateName({ target }: React.FocusEvent<HTMLInputElement>) {
    const loadingToast = toast.loading(t('toast:saving-changes'));
    await updateUser({ name: target.value });
    await dispatch(fetchAndSetUser());
    toast.dismiss(loadingToast);
    toast.success(t('toast:success'));
  }

  async function deleteAvatar() {
    await updateUser({ avatar: null });
    dispatch(fetchAndSetUser());
  }

  useEffect(() => {
    setUserName(user?.name || '');
  }, [user?.name]);

  useEffect(() => {
    setEmail(user?.email || '');
  }, [user?.email]);

  return (
    <>
      <div className={`rounded-xl ${!hideTitle && 'p-4'} flex flex-col w-full h-full`} style={{ minWidth: '25cqw' }}>
        <div className="bg" />
        {hideTitle !== true && (
          <>
            <h1 className="text-xl font-bold">{t('event:live.profile')}</h1>
            <hr />
          </>
        )}
        {user && (
          <div>
            <div className="flex flex-col">
              {hideAvatar !== true && (
                <>
                  <div className="flex justify-center">
                    <VyUploadCrop cropShape="round" onImage={(blob) => uploadAvatar(blob)}>
                      <VyAvatar
                        className="mt-5 mb-5 bg-black cursor-pointer large-avatar"
                        src={user?.avatar?._id ? createDisplayLink(user.avatar._id) : undefined}
                        style={{ width: '150px', height: '150px' }}>
                        <FaUpload />
                      </VyAvatar>
                    </VyUploadCrop>
                  </div>
                  {user.avatar && (
                    <Button aria-label="delete" className="w-full" onClick={deleteAvatar}>
                      <DeleteIcon fontSize="small" />
                    </Button>
                  )}
                </>
              )}
              {/* {(user?.profileStatus?.emoji || user?.profileStatus?.text) && (
                <div className="flex justify-center">
                  <p className="px-4 py-2 rounded-lg border-2 border-black">
                    {user?.profileStatus?.emoji ?? ''}&nbsp;&nbsp;&nbsp;&nbsp;{user?.profileStatus?.text ?? ''}
                  </p>
                </div>
              )} */}
              {!registration && <>{t('event:registration.no-registration-data')}</>}
              {!guestmanagementFields?.find((f) => f?.isUserName) && user.name && (
                <TextField
                  style={{ marginTop: 6 }}
                  onBlur={updateName}
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  label={event?.template?.individualNames?.username || t('event:live.profile-name')}
                  InputLabelProps={{ style: { color: textColor } }}
                  inputProps={{ style: { color: textColor } }}
                />
              )}

              {event?.modules?.guestmanagement?.settings?.authWithoutEmail !== true && !guestmanagementFields.find((field) => field.isEmailField) && (
                <TextField disabled style={{ marginTop: 6 }} value={email} label={t('event:live.profile-email')} />
              )}
            </div>
            <div className="flex flex-col w-full">
              <ProfileRegistrationFields autoSave onFormStateChange={onFormStateChange} />
              <ProfileFileManagement />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileFormModal;
